import React, { createContext, useState, useEffect, useRef } from "react";

export const AppContext = createContext();

const getApiBase = () => {
  const hostname = window.location.hostname;

  // Local PC Development
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8000/api";
  }
  
  // Mobile testing on Local Network (PC's IP)
  if (hostname.startsWith("10.") || hostname.startsWith("192.") || hostname.startsWith("172.")) {
    return `http://${hostname}:8000/api`;
  }

  // Production (Netlify / Custom Domain)
  return "https://selffo-backend.onrender.com/api";
};
const API_BASE = getApiBase();

export function AppProvider({ children }) {
  const [data, setData] = useState({
    posts: [],
    comments: [],
    currentUser: null
  });
  
  const [myPosts, setMyPosts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  const [token, setToken] = useState(localStorage.getItem("access_token"));
  const [selectedUniversity, setSelectedUniversity] = useState(null); 

  const fetchProfile = async () => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/me/`, { headers: getHeaders() });
    if (res.ok) {
      const profileData = await res.json();
      setData(prev => ({ 
        ...prev, 
        currentUser: { 
          username: profileData.username, 
          email: profileData.email,
          phone: profileData.phone,
          university: profileData.university,
          isAdmin: profileData.is_admin,
          avatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${profileData.avatar_seed || profileData.username}&backgroundColor=121214`, 
          stats: { 
            posts: profileData.post_count, 
            reactions: profileData.reaction_count 
          } 
        } 
      }));
    } else if (res.status === 401) {
      logout();
    }
  };

  const fetchMyPosts = async () => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/posts/mine/`, { headers: getHeaders() });
    if (res.ok) {
      const posts = await res.json();
      setMyPosts(posts);
    } else if (res.status === 401) {
      logout();
    }
  };

  const notifiedIdsRef = useRef(new Set());

  // Request browser notification permission
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const fetchNotifications = async () => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/notifications/`, { headers: getHeaders() });
    if (res.ok) {
      const data = await res.json();
      const notifs = data.results || data;
      setNotifications(notifs);

      // Trigger native browser push notifications for new unread ones (fallback if SW fails or is closed)
      if ("Notification" in window && Notification.permission === "granted") {
        notifs.forEach(notif => {
          if (!notif.is_read && !notifiedIdsRef.current.has(notif.id)) {
            // We skip manual Notification if service worker is active, to avoid double notification.
            // But we can keep track of IDs.
            notifiedIdsRef.current.add(notif.id);
          }
        });
      }
    } else if (res.status === 401) {
      logout();
    }
  };

  const VAPID_PUBLIC_KEY = "BC52KSkGuK06kLmMOHIe7wUA9jmF65ehxMiW2-WSEBmOGs4KT5N7_fT_WkFNFSLnYMTI9M8PXMEWwjsl-YjaKdY";

  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  const registerPushSubscription = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window && token) {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        let subscription = await registration.pushManager.getSubscription();
        
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
          });
        }
        
        // Send subscription to backend
        await fetch(`${API_BASE}/notifications/subscribe/`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(subscription)
        });
      } catch (err) {
        console.error("Push registration failed", err);
      }
    }
  };

  const markNotificationsRead = async () => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/notifications/mark_all_read/`, {
      method: 'POST',
      headers: getHeaders()
    });
    if (res.ok) {
      fetchNotifications();
    }
  };

  useEffect(() => {
    if (token) {
      localStorage.setItem("access_token", token);
      fetchProfile();
      fetchMyPosts();
      fetchNotifications();
      registerPushSubscription();
    } else {
      localStorage.removeItem("access_token");
      setData(prev => ({ ...prev, currentUser: null }));
      setMyPosts([]);
      setNotifications([]);
    }
  }, [token]);

  const getHeaders = () => {
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  };

  const updateProfile = async (updates) => {
    const res = await fetch(`${API_BASE}/me/`, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (res.ok) {
      await fetchProfile();
      return true;
    }
    return false;
  };

  const login = async (username, password) => {
    try {
      const res = await fetch(`${API_BASE}/token/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("username", username);
        setToken(data.access);
        return { success: true };
      }
      return { success: false, error: data.detail || "Invalid username or password" };
    } catch (err) {
      return { success: false, error: "Server connection failed" };
    }
  };

  const signup = async (username, password, university, email, phone = "") => {
    try {
      const res = await fetch(`${API_BASE}/register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, university, email, phone })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("username", data.username);
        setToken(data.access);
        return { success: true };
      }
      return { success: false, error: data.error || "Registration failed" };
    } catch (err) {
      return { success: false, error: "Server connection failed" };
    }
  };

  const googleLogin = async (googleToken) => {
    try {
      const res = await fetch(`${API_BASE}/google-login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: googleToken })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("username", data.username);
        setToken(data.access);
        return { success: true };
      }
      return { success: false, error: data.error || "Google login failed" };
    } catch (err) {
      return { success: false, error: "Server connection failed" };
    }
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem("username");
    localStorage.removeItem("access_token");
  };

  const [nextUrl, setNextUrl] = useState(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const fetchPosts = async (searchQuery = "", append = false) => {
    try {
      if (append && !nextUrl) return;
      if (append) setIsFetchingMore(true);

      let url = append ? nextUrl : `${API_BASE}/posts/`;
      
      if (!append) {
        const params = new URLSearchParams();
        if (selectedUniversity) params.append("university", selectedUniversity);
        if (searchQuery) params.append("search", searchQuery);
        // Force server to reshuffle the random selection on every refresh
        params.append("t", Date.now());
        const queryString = params.toString();
        url += queryString ? `?${queryString}` : "";
      }
      
      console.log("Fetching from: " + url);
      let headers = getHeaders();
      let res = await fetch(url, { headers });

      if (res.status === 401 && token) {
        console.warn("Token expired, retrying request anonymously...");
        logout();
        headers = { "Content-Type": "application/json" };
        res = await fetch(url, { headers });
      }

      if (res.ok) {
        const apiResponse = await res.json();
        const newPosts = apiResponse.results; // Paginated results
        
        setNextUrl(apiResponse.next); // URL for next page
        
        setData(prev => ({ 
          ...prev, 
          posts: append ? [...prev.posts, ...newPosts] : newPosts 
        }));
      } else {
        console.error("API responded with error: " + res.status);
      }
    } catch (e) {
      console.error("API Error", e);
    } finally {
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [selectedUniversity]); 

  const addPost = async (text, category, extraData = null) => {
    const res = await fetch(`${API_BASE}/posts/`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ text, category, ...extraData })
    });
    if (res.ok) {
      fetchPosts();
      fetchMyPosts(); // Refresh my posts too
    }
    return res.ok;
  };

  const addReaction = async (postId, emoji) => {
    const res = await fetch(`${API_BASE}/posts/${postId}/react/`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ emoji })
    });
    if (res.ok) fetchPosts();
    return res;
  };

  const addComment = async (postId, text) => {
    const res = await fetch(`${API_BASE}/posts/${postId}/comment/`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ text })
    });
    if (res.ok) fetchPosts();
    return res;
  };

  const value = {
    posts: data.posts,
    myPosts,
    currentUser: data.currentUser,
    token, 
    selectedUniversity,
    setSelectedUniversity,
    login,
    googleLogin,
    signup,
    logout,
    addPost,
    addReaction,
    addComment,
    fetchPosts,
    fetchMyPosts,
    updateProfile,
    notifications,
    fetchNotifications,
    markNotificationsRead,
    hasMore: !!nextUrl,
    isFetchingMore,
    fetchRandomPosts: async (append = true) => {
      try {
        setIsFetchingMore(true);
        // Add timestamp to prevent browser caching of the random response
        let url = `${API_BASE}/posts/random/?t=${Date.now()}`;
        if (selectedUniversity) url += `&university=${selectedUniversity}`;
        
        const res = await fetch(url, { headers: getHeaders() });
        if (res.ok) {
          const apiResponse = await res.json();
          const newPosts = apiResponse.results || apiResponse;
          
          setData(prev => {
            // Filter out posts that are already in the list to avoid duplicate keys
            const existingIds = new Set(prev.posts.map(p => p.id));
            const uniqueNewPosts = newPosts.filter(p => !existingIds.has(p.id));
            
            return { 
              ...prev, 
              posts: append ? [...prev.posts, ...uniqueNewPosts] : newPosts 
            };
          });
        }
      } catch (e) {
        console.error("Random API Error", e);
      } finally {
        setIsFetchingMore(false);
      }
    }
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
